import { useMemo } from 'react';
import { bg, type MoneyCents } from '@tragfaehigkeit/core';
import { de } from '../../../lib/i18n/de';
import { MoneyInput } from '../../../components/finance/MoneyInput';
import { BgCompositionEditor } from '../../../components/finance/BgCompositionEditor';
import { FieldHelp } from '../../../components/forms/FieldHelp';
import type {
  ResourcesBgMemberDraft,
  ResourcesDraft,
  ResourcesKduDraft,
} from '../../../lib/store/wizard';

interface BgStepProps {
  resources: ResourcesDraft;
  onAddMember: (member: Omit<ResourcesBgMemberDraft, 'id'>) => void;
  onUpdateMember: (memberId: string, patch: Partial<Omit<ResourcesBgMemberDraft, 'id'>>) => void;
  onRemoveMember: (memberId: string) => void;
  onKduChange: (patch: Partial<ResourcesKduDraft>) => void;
  onBruttoChange: (cents: number | null) => void;
  onSonstigesChange: (cents: number | null) => void;
}

/** Sub-Step 4.4: Bedarfsgemeinschaft (Members + KdU + Bedarfssumme live). */
export function BgStep({
  resources,
  onAddMember,
  onUpdateMember,
  onRemoveMember,
  onKduChange,
  onBruttoChange,
  onSonstigesChange,
}: BgStepProps) {
  const t = de.steps.resources.subSteps.bg;

  const bedarf = useMemo(() => {
    try {
      return bg.computeBgBedarf({
        members: resources.bgComposition.map((m) => ({
          role: m.role,
          age: m.age,
          specialNeeds: [...m.specialNeeds],
        })),
        kdu: {
          kaltmiete: resources.kdu.kaltCents as MoneyCents,
          nebenkosten: resources.kdu.nebenCents as MoneyCents,
          heizkosten: resources.kdu.heizCents as MoneyCents,
        },
      });
    } catch {
      return null;
    }
  }, [resources.bgComposition, resources.kdu]);

  return (
    <div className="space-y-10">
      <p className="max-w-prose font-serif text-base italic text-stone-500 md:text-lg">{t.intro}</p>

      <div>
        <BgCompositionEditor
          members={resources.bgComposition}
          onAdd={onAddMember}
          onUpdate={onUpdateMember}
          onRemove={onRemoveMember}
        />
        <div className="mt-2">
          <FieldHelp pathKey="resources.bg.composition" />
        </div>
      </div>

      <fieldset className="space-y-6 border-t border-stone-200 pt-8">
        <legend className="flex items-start gap-2 font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
          {t.kduLabel}
          <FieldHelp pathKey="resources.bg.kdu" className="-mt-1.5" />
        </legend>

        <MoneyInput
          valueCents={resources.kdu.kaltCents || null}
          onChange={(v) => {
            onKduChange({ kaltCents: v ?? 0 });
          }}
          label={t.kduKaltLabel}
          placeholder="z.B. 650,00"
        />
        <MoneyInput
          valueCents={resources.kdu.nebenCents || null}
          onChange={(v) => {
            onKduChange({ nebenCents: v ?? 0 });
          }}
          label={t.kduNebenLabel}
          placeholder="z.B. 150,00"
        />
        <MoneyInput
          valueCents={resources.kdu.heizCents || null}
          onChange={(v) => {
            onKduChange({ heizCents: v ?? 0 });
          }}
          label={t.kduHeizLabel}
          placeholder="z.B. 90,00"
        />
      </fieldset>

      <div className="border-t border-stone-200 pt-6">
        <MoneyInput
          valueCents={bedarf ? bedarf.totalBedarfCents : null}
          onChange={() => {
            // read-only
          }}
          label={t.bedarfLabel}
          hint={t.bedarfHint}
          readOnly
        />
      </div>

      <fieldset className="space-y-6 border-t border-stone-200 pt-8">
        <legend className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
          {t.incomeSectionLabel}
        </legend>
        <p className="max-w-prose font-serif text-sm italic text-stone-500">
          {t.incomeSectionHint}
        </p>
        <MoneyInput
          valueCents={resources.bruttoErwerbseinkommenCents}
          onChange={onBruttoChange}
          label={t.bruttoLabel}
          hint={t.bruttoHint}
          placeholder="z.B. 1.800,00"
        />
        <MoneyInput
          valueCents={resources.sonstigesEinkommenCents}
          onChange={onSonstigesChange}
          label={t.sonstigesLabel}
          hint={t.sonstigesHint}
          placeholder="z.B. 500,00"
        />
      </fieldset>
    </div>
  );
}
